import React, { useState } from 'react';
import { TrendingUp, Mail, Lock, ArrowRight, Eye, EyeOff, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import authService from '../services/auth.service';

export default function Login({ onLogin, onSwitchToSignUp, onForgotPassword }) {
  const { login, verifyOTP, loading, error } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [otp, setOtp] = useState('');
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendingOtp, setResendingOtp] = useState(false);

  const [localError, setLocalError] = useState('');
  const [localSuccess, setLocalSuccess] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    setLocalSuccess('');
    
    if (!email || !password) {
      setLocalError('Please fill in all fields');
      return;
    }

    const result = await login({ email, password });
    
    if (result.success) {
      setLocalSuccess('Login successful! Redirecting...');
      onLogin(result.data.userId);
    } else {
      if (result.error && result.error.includes('verify your account')) {
        setUnverifiedEmail(email);
        setShowOtpScreen(true);
        setOtpSuccess('Please verify your email to continue');
      } else {
        setLocalError(result.error || 'Login failed. Please check your credentials.');
      }
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setOtpError('');
    setOtpSuccess('');
    
    if (!otp || otp.length !== 6) {
      setOtpError('Please enter a valid 6-digit code');
      return;
    }

    const result = await verifyOTP(unverifiedEmail, otp);
    
    if (result.success) {
      setOtpSuccess('Email verified successfully!');
      setTimeout(() => {
        setShowOtpScreen(false);
        setOtp('');
        setUnverifiedEmail('');
        setPassword('');
        setLocalSuccess('Please login with your credentials');
      }, 1500);
    } else {
      setOtpError(result.error || 'Invalid code. Please try again.');
    }
  };

  const handleResendOtp = async () => {
    setResendingOtp(true);
    setOtpError('');
    setOtpSuccess('');
    try {
      await authService.resendOTP(unverifiedEmail);
      setOtpSuccess('New code sent to your email');
      setOtp('');
    } catch (err) {
      setOtpError(err.response?.data?.message || 'Failed to resend code. Please try again.');
    } finally {
      setResendingOtp(false);
    }
  };

  const handleBackToLogin = () => {
    setShowOtpScreen(false);
    setOtp('');
    setUnverifiedEmail('');
    setOtpError('');
    setOtpSuccess('');
  };

  // OTP Verification Screen
  if (showOtpScreen) {
    return (
      <div className="min-h-screen bg-[#0b141a] flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="bg-[#1f2c34] rounded-3xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-[#00a884] p-6 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-white/20 rounded-2xl mb-3 backdrop-blur-sm">
                <Mail className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-xl font-light text-white mb-2">Verify Your Email</h1>
              <p className="text-xs text-white/80">Code sent to</p>
              <p className="text-xs text-white font-medium mt-1">{unverifiedEmail}</p>
            </div>

            {/* Form */}
            <div className="p-5">
              {otpSuccess && (
                <div className="mb-4 bg-[#00a884]/10 border border-[#00a884]/20 rounded-lg p-3 flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#00a884] flex-shrink-0 mt-0.5" />
                  <p className="text-[#00a884] text-sm flex-1">{otpSuccess}</p>
                </div>
              )}

              {(error || otpError) && (
                <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-300 text-sm flex-1">{otpError || error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-[#8696a0] text-sm mb-2">Enter Code</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setOtp(value);
                      if (otpError) setOtpError('');
                    }}
                    placeholder="000000"
                    maxLength={6}
                    disabled={loading}
                    className="w-full bg-[#2a3942] text-white text-center text-2xl tracking-[0.5em] placeholder-[#8696a0] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00a884] disabled:opacity-50 border border-[#2a3942]"
                  />
                  <p className="text-[#8696a0] text-xs mt-2 text-center">
                    6-digit code from your email
                  </p>
                </div>

                <button
                  onClick={handleVerifyOtp}
                  disabled={loading || otp.length !== 6}
                  className="w-full bg-[#00a884] text-white font-medium rounded-lg px-6 py-3 active:bg-[#008f6d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
                  ) : (
                    'Verify Email'
                  )}
                </button>

                <div className="text-center">
                  <p className="text-[#8696a0] text-sm mb-2">Didn't receive the code?</p>
                  <button
                    onClick={handleResendOtp}
                    disabled={resendingOtp || loading}
                    className="text-[#00a884] hover:underline font-medium text-sm disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${resendingOtp ? 'animate-spin' : ''}`} />
                    {resendingOtp ? 'Sending...' : 'Resend Code'}
                  </button>
                </div>

                <div className="text-center pt-4 border-t border-[#2a3942]">
                  <button
                    onClick={handleBackToLogin}
                    disabled={loading}
                    className="text-[#8696a0] hover:text-white transition-colors text-sm disabled:opacity-50"
                  >
                    ← Back to Login
                  </button>
                </div>
              </div>
            </div>
          </div>

          <p className="text-center text-[#8696a0] text-xs mt-4">Code expires in 10 minutes</p>
        </div>
      </div>
    );
  }

  // Login Screen
  return (
    <div className="min-h-screen bg-[#0b141a] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo/Brand */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#00a884] rounded-3xl mb-3 shadow-lg shadow-[#00a884]/20">
            <TrendingUp className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-light text-white mb-1">UtajiriHub</h1>
          <p className="text-[#8696a0] text-xs">Track and grow your investments</p>
        </div>

        {/* Login Card */}
        <div className="bg-[#1f2c34] rounded-3xl overflow-hidden shadow-2xl">
          <div className="p-5">
            {localSuccess && (
              <div className="mb-4 bg-[#00a884]/10 border border-[#00a884]/20 rounded-lg p-3 flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#00a884] flex-shrink-0 mt-0.5" />
                <p className="text-[#00a884] text-sm flex-1">{localSuccess}</p>
              </div>
            )}

            {(error || localError) && (
              <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-300 text-sm flex-1">{localError || error}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-[#8696a0] text-sm mb-2">Email</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="w-5 h-5 text-[#8696a0]" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (localError) setLocalError('');
                    }}
                    placeholder="you@example.com"
                    disabled={loading}
                    className="w-full bg-[#2a3942] text-white placeholder-[#8696a0] rounded-lg pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00a884] disabled:opacity-50 border border-[#2a3942]"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-[#8696a0] text-sm mb-2">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="w-5 h-5 text-[#8696a0]" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (localError) setLocalError('');
                    }}
                    placeholder="Enter your password"
                    disabled={loading}
                    className="w-full bg-[#2a3942] text-white placeholder-[#8696a0] rounded-lg pl-10 pr-12 py-3 focus:outline-none focus:ring-2 focus:ring-[#00a884] disabled:opacity-50 border border-[#2a3942]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-[#8696a0] hover:text-white transition-colors"
                    disabled={loading}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Remember & Forgot */}
              <div className="flex items-center justify-between">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                    className="w-4 h-4 rounded border-[#2a3942] bg-[#2a3942] text-[#00a884] focus:ring-2 focus:ring-[#00a884] focus:ring-offset-0 disabled:opacity-50"
                  />
                  <span className="ml-2 text-[#8696a0] text-sm">Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={onForgotPassword}
                  disabled={loading}
                  className="text-[#00a884] hover:underline text-sm font-medium disabled:opacity-50"
                >
                  Forgot?
                </button>
              </div>

              {/* Login Button */}
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full bg-[#00a884] text-white font-medium rounded-lg px-6 py-3 flex items-center justify-center gap-2 active:bg-[#008f6d] transition-colors shadow-lg shadow-[#00a884]/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>

            {/* Sign Up Link */}
            <div className="mt-6 text-center pt-6 border-t border-[#2a3942]">
              <p className="text-[#8696a0] text-sm mb-3">
                Don't have an account?
              </p>
              <button 
                onClick={onSwitchToSignUp}
                disabled={loading}
                className="w-full bg-[#2a3942] text-[#00a884] font-medium rounded-lg px-6 py-3 border-2 border-[#00a884] hover:bg-[#00a884] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Account
              </button>
            </div>
          </div>
        </div>

        <p className="text-center text-[#8696a0] text-xs mt-4">
          🔒 Your data is secured with end-to-end encryption
        </p>
      </div>
    </div>
  );
}